/**
 * 計算フィールド 計算エンジン（#3145）の字句解析。
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */
import { calculationErrorCode } from './constants';
import { CalculationFormulaError } from './calculationError';

export type TokenType =
  | 'number'
  | 'string'
  | 'fieldRef'
  | 'funcName'
  | 'operator'
  | 'lparen'
  | 'rparen'
  | 'comma';

export interface Token {
  type: TokenType;
  /** number: 数値文字列 / string: エスケープ解決済みの値 / fieldRef: フィールドコード */
  value: string;
  /** 元の式におけるトークンの開始位置 */
  position: number;
  /** 元の式におけるトークンの終了位置（終端の次のインデックス） */
  end: number;
}

const WHITESPACE_PATTERN = /[ \t\r\n]/;
const DIGIT_PATTERN = /[0-9]/;
const IDENTIFIER_START_PATTERN = /[A-Za-z_]/;
const IDENTIFIER_PART_PATTERN = /[A-Za-z0-9_]/;
const OPERATOR_CHARS = ['+', '-', '*', '/', '^', '&'];

/**
 * 比較演算子（#3358）。2文字のものを先に並べる（<= を < と = に割らないため）。
 * 結果は 1 / 0 の数値で返す（設計書 §5.1-①）。
 */
const COMPARISON_OPERATORS = ['<=', '>=', '!=', '=', '<', '>'];

/** 計算式をトークン列に分解する。解釈できない文字があれば構文エラーを投げる */
export const tokenize = (formula: string): Token[] => {
  const tokens: Token[] = [];
  let position = 0;

  while (position < formula.length) {
    const char = formula[position];

    if (WHITESPACE_PATTERN.test(char)) {
      position++;
      continue;
    }

    if (DIGIT_PATTERN.test(char)) {
      const start = position;
      while (position < formula.length && DIGIT_PATTERN.test(formula[position])) {
        position++;
      }
      if (formula[position] === '.') {
        position++;
        if (!DIGIT_PATTERN.test(formula[position] ?? '')) {
          throw new CalculationFormulaError(
            calculationErrorCode.syntax,
            `小数点の後に数字がありません: ${formula.slice(start, position)}`,
          );
        }
        while (position < formula.length && DIGIT_PATTERN.test(formula[position])) {
          position++;
        }
      }
      tokens.push({
        type: 'number',
        value: formula.slice(start, position),
        position: start,
        end: position,
      });
      continue;
    }

    if (char === '"') {
      const start = position;
      position++;
      let value = '';
      let closed = false;
      while (position < formula.length) {
        const current = formula[position];
        if (current === '\\') {
          const next = formula[position + 1];
          if (next === '"' || next === '\\') {
            // エスケープ（\" と \\）のみ解決する
            value += next;
            position += 2;
          } else {
            // 正規表現用のバックスラッシュ（\d 等）はそのまま残す
            value += current;
            position++;
          }
          continue;
        }
        if (current === '"') {
          closed = true;
          position++;
          break;
        }
        value += current;
        position++;
      }
      if (!closed) {
        throw new CalculationFormulaError(
          calculationErrorCode.syntax,
          '文字列が閉じられていません',
        );
      }
      tokens.push({ type: 'string', value, position: start, end: position });
      continue;
    }

    if (char === '{') {
      const start = position;
      const closeIndex = formula.indexOf('}', position + 1);
      if (closeIndex === -1) {
        throw new CalculationFormulaError(
          calculationErrorCode.syntax,
          '項目参照が閉じられていません',
        );
      }
      const fieldCode = formula.slice(position + 1, closeIndex);
      if (fieldCode === '') {
        throw new CalculationFormulaError(calculationErrorCode.syntax, '項目参照が空です');
      }
      position = closeIndex + 1;
      tokens.push({ type: 'fieldRef', value: fieldCode, position: start, end: position });
      continue;
    }

    if (IDENTIFIER_START_PATTERN.test(char)) {
      const start = position;
      while (position < formula.length && IDENTIFIER_PART_PATTERN.test(formula[position])) {
        position++;
      }
      tokens.push({
        type: 'funcName',
        value: formula.slice(start, position),
        position: start,
        end: position,
      });
      continue;
    }

    // 比較演算子（#3358）。2文字を先に試すため OPERATOR_CHARS より前に置く
    const comparison = COMPARISON_OPERATORS.find(op => formula.startsWith(op, position));
    if (comparison !== undefined) {
      tokens.push({
        type: 'operator',
        value: comparison,
        position,
        end: position + comparison.length,
      });
      position += comparison.length;
      continue;
    }

    if (OPERATOR_CHARS.includes(char)) {
      tokens.push({ type: 'operator', value: char, position, end: position + 1 });
      position++;
      continue;
    }

    if (char === '(') {
      tokens.push({ type: 'lparen', value: char, position, end: position + 1 });
      position++;
      continue;
    }

    if (char === ')') {
      tokens.push({ type: 'rparen', value: char, position, end: position + 1 });
      position++;
      continue;
    }

    if (char === ',') {
      tokens.push({ type: 'comma', value: char, position, end: position + 1 });
      position++;
      continue;
    }

    throw new CalculationFormulaError(
      calculationErrorCode.syntax,
      `使用できない文字があります: ${char}`,
    );
  }

  return tokens;
};
